import { AsyncPipe, CommonModule } from '@angular/common'
import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Subject, takeUntil } from 'rxjs'
import { ViewAllNotificationModalComponent } from '../view-all-notification-modal/view-all-notification-modal.component'
import { NotificationService } from '../services/notification.service'
import {
    Notification,
    TopNotificationsResponse,
} from '../model/notification.model'
import { LoadingDirective } from 'src/app/directive/loader.directive'

interface DisplayNotification {
    id: string
    type: 'incoming_sms' | 'incoming_email' | 'follow'
    user: {
        name: string
        avatar: string
    }
    patientId: string
    message: string
    subject?: string
    timeAgo: string
    read: boolean
    hasMMSAttachment:boolean;
}

@Component({
    standalone: true,
    imports: [CommonModule, AsyncPipe, LoadingDirective],
    selector: 'app-notification-model',
    templateUrl: './notification-model.component.html',
    styleUrls: ['./notification-model.component.css'],
})
export class NotificationListComponent implements OnInit, OnDestroy {
    notifications: DisplayNotification[] = []
    @Output() closeModal = new EventEmitter<void>()
    private destroy$ = new Subject<void>() 
    isLoading = false
    constructor(
        private dialog: MatDialog,
        public notificationService: NotificationService,
    ) {}

    ngOnInit(): void {
        this.notifications = [];
        this.isLoading = true
        this.notificationService
            .getUnreadCount()
            .pipe(takeUntil(this.destroy$))
            .subscribe()
        this.notificationService
            .getTopNotifications()
            .pipe(takeUntil(this.destroy$))
            .subscribe((notifications: TopNotificationsResponse) => {
                this.notifications = this.mapToDisplayNotifications(
                    notifications.data,
                )
                this.isLoading = false
            })

        this.notificationService.realTimeNotification$
            .pipe(takeUntil(this.destroy$))
            .subscribe((notifications: any) => {
                this.notifications =
                    this.mapToDisplayNotifications(notifications)
            })
    }

    private mapToDisplayNotifications(
        apiNotifications: Notification[],
    ): DisplayNotification[] {
        return apiNotifications.map((n) => ({
            id: n.id,
            type: n.type,
            user: {
                name: n.patientName,
                avatar: n.patientProfileUrl,
            },
            patientId: n.patientId,
            message: n.content,
            subject: n.emailSubject ?? undefined,
            timeAgo: this.calculateTimeAgo(n.createdDateTimeUtc),
            read: n.isRead === 1,
            hasMMSAttachment: n.hasMMSAttachment
        }))
    }

    private calculateTimeAgo(date: string): string {
        const now = new Date()
        const notificationDate = new Date(date)
        const diffMs = now.getTime() - notificationDate.getTime()
        const diffMins = Math.round(diffMs / 60000)
        if (diffMins < 60)
            return `${diffMins} min${diffMins !== 1 ? '' : ''} ago`
        const diffHours = Math.round(diffMins / 60)
        if (diffHours < 24)
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
        const diffDays = Math.round(diffHours / 24)
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    }

    markAllAsRead(): void {
        this.notifications = []
        this.notificationService
            .markAllAsRead()
            .pipe(takeUntil(this.destroy$))
            .subscribe()
    }

    viewAllNotifications(): void {
        this.dialog.open(ViewAllNotificationModalComponent, {
            width: '577px',
            panelClass: 'un-follower-dialog-container',
        })
        this.closeModal.emit()
    }

    openMyConversation(notificationId: string): void {
        this.notificationService.openMyConversation(notificationId)
        this.closeModal.emit()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }
}
